import { el, clear } from "./dom.js";
import { magButton } from "./magazine/kit.js";
import { expandableResultCard } from "./search-result.js";
import { searchGames } from "../api/igdb.js";
import { searchBooks } from "../api/books.js";
import { searchTv } from "../api/tv.js";
import { searchAnime, enrichAnime } from "../api/anime.js";
import { searchMovies, getMovie } from "../api/movies.js";
import { EXTERNAL_KEY, catalogView } from "../utils/overrides.js";

// "Re-link" modal: search the item's own source (IGDB / Open Library / TVmaze /
// OMDb) the way the Add flow does, pick an entry with "Use this", confirm.
// Resolves to { fresh, taxonomy?, enrichFailed? } — `fresh` is the chosen entry
// in the source's shape (what utils/overrides.js catalogPatchFrom consumes) — or
// null. Movies: the search row is thin, so the full OMDb record is fetched on
// confirm (best effort — the row itself is used if that fails).

const SEARCH = {
	videogame: (q, opts) => searchGames(q, opts),
	book: (q, opts) => searchBooks(q, opts),
	tv: (q, opts) => searchTv(q, opts),
	anime: (q, opts) => searchAnime(q, opts),
	movie: (q, opts) => searchMovies(q, opts),
};

const PLACEHOLDER = {
	videogame: "Search IGDB…",
	book: "Search Open Library…",
	tv: "Search TVmaze…",
	anime: "Search TVmaze (anime)…",
	movie: "Search OMDb (title or IMDb id)…",
};

const SOURCE_NAME = {
	videogame: "IGDB",
	book: "Open Library",
	tv: "TVmaze",
	anime: "TVmaze",
	movie: "OMDb",
};

// Normalise a result into what the card needs; the raw result stays `fresh`.
function cardProps(mediaType, r) {
	if (mediaType === "videogame") {
		return {
			title: r.name,
			subtitle: r.release_year ? String(r.release_year) : "",
			summary: r.summary ?? "",
			details: [
				{ label: "Platforms", value: (r.platforms ?? []).join(", ") },
				{ label: "Genres", value: (r.genres ?? []).join(", ") },
			],
		};
	}
	if (mediaType === "book") {
		const bits = [(r.authors ?? []).join(", "), r.published_year ? String(r.published_year) : ""].filter(Boolean);
		return { title: r.title, subtitle: bits.join(" · "), summary: r.summary ?? "", details: [] };
	}
	if (mediaType === "movie") {
		return {
			title: r.title,
			subtitle: [r.release_year ? String(r.release_year) : "", r.imdb_id ?? ""].filter(Boolean).join(" · "),
			summary: r.summary ?? "",
			details: [],
		};
	}
	return {
		title: r.title,
		subtitle: r.premiered_year ? String(r.premiered_year) : "",
		summary: r.summary ?? "",
		details: [],
	};
}

function displayTitle(mediaType, r) {
	const t = mediaType === "videogame" ? r.name : r.title;
	const year = r.release_year ?? r.published_year ?? r.premiered_year ?? null;
	return year ? `${t} (${year})` : t;
}

export function showRelinkSheet({ mediaType, record }) {
	return new Promise((resolve) => {
		let settled = false;
		let inflight = null;
		const finish = (value) => {
			if (settled) return;
			settled = true;
			inflight?.abort();
			overlay.remove();
			document.removeEventListener("keydown", onKey);
			resolve(value);
		};
		const onKey = (e) => {
			if (e.key === "Escape") finish(null);
		};
		document.addEventListener("keydown", onKey);

		const key = EXTERNAL_KEY[mediaType];
		const currentExt = String(record[key]);
		const currentTitle = catalogView(record).title ?? record.title ?? "";

		const results = el("div", {
			style: { display: "flex", flexDirection: "column", gap: "9px", marginTop: "10px" },
		});
		const status = el("div.modal__hint", { style: { minHeight: "1em" } });

		let timer = null;
		const input = el("input.field__select", {
			type: "search",
			placeholder: PLACEHOLDER[mediaType] ?? "Search…",
			value: currentTitle,
			oninput: () => {
				clearTimeout(timer);
				timer = setTimeout(() => runSearch(input.value.trim()), 300);
			},
		});

		async function runSearch(q) {
			inflight?.abort();
			clear(results);
			if (q.length < 2) {
				status.textContent = "";
				return;
			}
			inflight = new AbortController();
			status.textContent = "Searching…";
			try {
				const list = await SEARCH[mediaType](q, { signal: inflight.signal });
				status.textContent = list.length ? "" : "No matches.";
				for (const r of list) {
					const isCurrent = String(r[key]) === currentExt;
					results.append(
						expandableResultCard({
							cover_url: r.cover_url ?? null,
							mediaType,
							...cardProps(mediaType, r),
							added: isCurrent,
							addLabel: "Use this",
							addedLabel: "✓ Current",
							onToggleAdd: async () => {
								if (isCurrent) return true;
								confirmPick(r);
								return false;
							},
						}),
					);
				}
			} catch (err) {
				if (err?.name === "AbortError") return;
				status.textContent = `Search failed: ${err?.message ?? err}`;
			}
		}

		// Step 2: spell out what changes, then resolve.
		function confirmPick(r) {
			const panel = el(
				"div",
				{},
				el("h2.modal__title", {}, "Re-link this item?"),
				el(
					"p.modal__hint",
					{},
					`Catalog details of "${currentTitle}" will be replaced by those of "${displayTitle(mediaType, r)}".`,
				),
				el(
					"p.modal__hint",
					{},
					"Your status, rating, awards, note, formats, editions, season/episode marks and groups stay. Fields you edited stay too.",
				),
				mediaType === "tv" || mediaType === "anime"
					? el("p.modal__hint", {}, "Season and episode marks are kept by number; they may not line up with a different show.")
					: null,
				el(
					"div.modal__actions",
					{ style: { padding: "0" } },
					magButton("Back", { variant: "ghost", onClick: () => render(searchPanel) }),
					magButton("Re-link", {
						onClick: async () => {
							if (mediaType === "movie") {
								render(el("p.modal__hint", { style: { padding: "20px 0" } }, "Fetching details from OMDb…"));
								let fresh = r;
								try {
									fresh = { ...(await getMovie(r.imdb_id)), imdb_id: r.imdb_id };
								} catch {
									// keep the search row; relinkRecord clears the stale extras
								}
								finish({ fresh });
								return;
							}
							if (mediaType !== "anime") {
								finish({ fresh: r });
								return;
							}
							render(el("p.modal__hint", { style: { padding: "20px 0" } }, "Fetching genres from MAL…"));
							let taxonomy;
							let enrichFailed = false;
							try {
								taxonomy = await enrichAnime(r.title, r.premiered_year);
							} catch {
								enrichFailed = true;
							}
							finish({ fresh: r, taxonomy, enrichFailed });
						},
					}),
				),
			);
			render(panel);
		}

		const searchPanel = el(
			"div",
			{},
			el("h2.modal__title", {}, "Re-link"),
			el(
				"p.modal__hint",
				{},
				`Pick the ${SOURCE_NAME[mediaType] ?? "source"} entry this should point at.`,
			),
			input,
			status,
			results,
			el(
				"div.modal__actions",
				{ style: { padding: "0" } },
				magButton("Cancel", { variant: "ghost", onClick: () => finish(null) }),
			),
		);

		const modal = el("div.modal", { style: { maxHeight: "90vh", overflowY: "auto" } });
		function render(panel) {
			clear(modal);
			modal.append(panel);
		}
		const overlay = el(
			"div.modal-overlay",
			{
				onclick: (e) => {
					if (e.target === overlay) finish(null);
				},
			},
			modal,
		);
		render(searchPanel);
		document.body.appendChild(overlay);
		queueMicrotask(() => {
			input.focus();
			input.select();
			runSearch(input.value.trim());
		});
	});
}
